import {IntlShape} from "react-intl";
import * as yup from "yup";

/**
 * Reads an amount typed by a person into integer cents, without floating point numbers. German
 * notation is expected ("12.345,67"); a plain dot as decimal separator ("12345.67") is accepted too.
 * Returns null if the text is not an amount with at most two decimal places.
 */
export const parseAmountToCents = (input: string): number | null => {
    const text = input.trim().replace(/\s/g, '');
    let normalized: string;
    if (/^-?\d{1,3}(\.\d{3})+(,\d{1,2})?$/.test(text)) {
        normalized = text.replace(/\./g, '').replace(',', '.');
    } else if (/^-?\d+(,\d{1,2})?$/.test(text)) {
        normalized = text.replace(',', '.');
    } else if (/^-?\d+\.\d{1,2}$/.test(text)) {
        normalized = text;
    } else {
        return null;
    }
    const negative = normalized.startsWith('-');
    const [integerPart, decimalPart = ''] = normalized.replace('-', '').split('.');
    const cents = Number(integerPart) * 100 + Number(decimalPart.padEnd(2, '0'));
    return negative ? -cents : cents;
};

/**
 * Writes cents the way the amount field expects them, e.g. 1234567 as "12345,67".
 */
export const formatCentsForInput = (cents: number): string => {
    const absolute = Math.abs(cents);
    const integerPart = Math.floor(absolute / 100);
    const decimalPart = String(absolute % 100).padStart(2, '0');
    return (cents < 0 ? '-' : '') + integerPart + ',' + decimalPart;
};

type YupAmountOptions = {
    optional?: boolean // an empty field is valid
    nonNegative?: boolean // the amount must not be below zero
};

/**
 * The validation of a field holding an amount typed by a person, see parseAmountToCents.
 */
export const yupAmount = (intl: IntlShape, {optional = false, nonNegative = false}: YupAmountOptions = {}) => {
    const schema = optional ? yup.string() : yup.string().required(intl.formatMessage({id: 'validation.field.required'}));
    return schema.test('amount', intl.formatMessage({id: 'validation.field.invalidAmount'}), value => {
        if (optional && (value ?? '').trim() === '') {
            return true;
        }
        const cents = parseAmountToCents(value ?? '');
        return cents !== null && (!nonNegative || cents >= 0);
    });
};
